import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { ButtonPro, Typography } from 'components';
import { agent } from 'services/service';
import {
  LeaveAllocationEmployeeDTO,
  MaternityAllocationDTO
} from 'services/LeaveAllocation';
import { toast } from 'react-toastify';
import { RootState, useAppSelector } from 'store';
import './index.scss';

const CURRENT_YEAR = new Date().getFullYear();

export const MaternityLeaveAllocationForm = () => {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [employees, setEmployees] = useState<LeaveAllocationEmployeeDTO[]>([]);
  const [allocations, setAllocations] = useState<MaternityAllocationDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUpn, setSelectedUpn] = useState('');
  const [days, setDays] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hrEmail, setHrEmail] = useState<string | null>(null);

  const currentUpn = useAppSelector(
    (state: RootState) => state.auth?.user?.authData?.upn
  );

  // Admins may view maternity allocations; only the assigned HR manager may change them.
  const canModify =
    !!hrEmail &&
    !!currentUpn &&
    hrEmail.toLowerCase() === String(currentUpn).toLowerCase();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [allocationResponse, employeeResponse] = await Promise.all([
        agent.LeaveAllocationService.getMaternityAllocations(year),
        agent.LeaveAllocationService.getEmployeeAllocations(year)
      ]);
      setAllocations(allocationResponse.data);
      setEmployees(employeeResponse.data);

      try {
        const hrResponse = await agent.HrManagerService.get();
        setHrEmail(hrResponse.data.email);
      } catch {
        setHrEmail(null);
      }
    } catch {
      toast.error('Could not load maternity allocations.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return employees
      .filter(
        (e) =>
          e.employeeName.toLowerCase().includes(term) ||
          e.epfNumber.toLowerCase().includes(term) ||
          e.upn.toLowerCase().includes(term)
      )
      .slice(0, 8);
  }, [search, employees]);

  const selected = employees.find((e) => e.upn === selectedUpn);

  const openModal = (upn?: string, existingDays?: number) => {
    setSelectedUpn(upn ?? '');
    setSearch(upn ? '' : '');
    setDays(existingDays !== undefined ? String(existingDays) : '');
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedUpn('');
    setSearch('');
    setDays('');
    setError(null);
  };

  const handleSave = async () => {
    if (!selectedUpn) {
      setError('Select an employee.');
      return;
    }

    const parsed = parseFloat(days);
    if (days === '' || isNaN(parsed) || parsed < 0) {
      setError('Enter a valid number of days.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await agent.LeaveAllocationService.setMaternityAllocation({
        upn: selectedUpn,
        year,
        days: parsed
      });
      toast.success(response.data.message);
      closeModal();
      await load();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          'Could not save the maternity allocation. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (row: MaternityAllocationDTO) => {
    if (row.usedDays > 0) {
      toast.warning(
        `${row.employeeName} has ${row.usedDays} day(s) of maternity leave pending or approved. ` +
          'Reject or cancel those leave requests first, then the allocation can be removed.'
      );
      return;
    }

    try {
      const response = await agent.LeaveAllocationService.setMaternityAllocation({
        upn: row.upn,
        year,
        days: 0
      });
      toast.success(response.data.message);
      await load();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? 'Could not remove the allocation.'
      );
    }
  };

  return (
    <div className="maternity-wrapper">
      <div className="maternity-wrapper__header">
        <Typography type="heading2" text="Maternity Leave Allocation" />
        <div className="maternity-wrapper__year">
          <Typography type="base_medium" text="Year" />
          <input
            type="number"
            className="maternity-wrapper__year-input"
            value={year}
            min={2020}
            max={2100}
            onChange={(e) => {
              const next = parseInt(e.target.value, 10);
              if (!isNaN(next)) setYear(next);
            }}
          />
        </div>
      </div>

      <section className="maternity-panel">
      <div className="maternity-panel__header">
        <div>
          <Typography type="base_medium" text="Maternity Leave" />
          <span className="maternity-panel__subtitle">
            Granted to individual employees. Only those listed here can apply.
            {!canModify && ' View only — the assigned HR manager can make changes.'}
          </span>
        </div>
        {canModify && (
          <ButtonPro
            label="Allocate Maternity Leave"
            onClick={() => openModal()}
            htmlType="button"
          />
        )}
      </div>

      {loading ? (
        <div className="maternity-panel__empty">
          <CircularProgress size={20} />
        </div>
      ) : allocations.length === 0 ? (
        <div className="maternity-panel__empty">
          No maternity leave allocated for {year}.
        </div>
      ) : (
        <table className="maternity-panel__table">
          <thead>
            <tr>
              <th>EPF</th>
              <th>Employee</th>
              <th>Days</th>
              <th>Used</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {allocations.map((row) => (
              <tr key={row.upn}>
                <td>{row.epfNumber}</td>
                <td>{row.employeeName}</td>
                <td>{row.days}</td>
                <td>{row.usedDays}</td>
                <td className="maternity-panel__row-actions">
                  {canModify && (
                    <>
                      <ButtonPro
                        label="Edit"
                        type="text"
                        htmlType="button"
                        onClick={() => openModal(row.upn, row.days)}
                      />
                      <ButtonPro
                        label="Remove"
                        type="text"
                        htmlType="button"
                        onClick={() => handleRemove(row)}
                      />
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modalOpen && (
        <div className="maternity-modal__backdrop" role="presentation">
          <div
            className="maternity-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Allocate maternity leave"
          >
            <Typography type="base_medium" text={`Allocate Maternity Leave — ${year}`} />

            {selected ? (
              <div className="maternity-modal__selected">
                <span>
                  {selected.employeeName}
                  {selected.epfNumber ? ` (${selected.epfNumber})` : ''}
                </span>
                <ButtonPro
                  label="Change"
                  type="text"
                  htmlType="button"
                  onClick={() => setSelectedUpn('')}
                />
              </div>
            ) : (
              <>
                <label className="maternity-modal__label" htmlFor="maternity-search">
                  Employee
                </label>
                <input
                  id="maternity-search"
                  className="maternity-modal__input"
                  placeholder="Search by name, EPF or email"
                  value={search}
                  autoComplete="off"
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setError(null);
                  }}
                />
                {matches.length > 0 && (
                  <ul className="maternity-modal__results">
                    {matches.map((e) => (
                      <li key={e.upn}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedUpn(e.upn);
                            setSearch('');
                          }}
                        >
                          <strong>{e.employeeName}</strong>
                          <span>{e.epfNumber}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            <label className="maternity-modal__label" htmlFor="maternity-days">
              Number of days
            </label>
            <input
              id="maternity-days"
              className="maternity-modal__input"
              type="number"
              min={0}
              step={0.5}
              placeholder="e.g. 84"
              value={days}
              onChange={(e) => {
                setDays(e.target.value);
                setError(null);
              }}
            />

            {error && <span className="maternity-modal__error">{error}</span>}

            <div className="maternity-modal__actions">
              <ButtonPro
                label="Cancel"
                type="white"
                htmlType="button"
                onClick={closeModal}
                disabled={saving}
              />
              <ButtonPro
                label={saving ? 'Saving...' : 'Save'}
                htmlType="button"
                onClick={handleSave}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      )}
      </section>
    </div>
  );
};
